import { CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DecisionContent } from "@/types/hld-wizard";

interface DecisionStepperProps {
  decisions: DecisionContent[];
  currentStep: number;
  completedSteps: Set<number>;
  onNavigate: (step: number) => void;
}

export function DecisionStepper({
  decisions,
  currentStep,
  completedSteps,
  onNavigate,
}: DecisionStepperProps) {
  const current = decisions[currentStep];

  return (
    <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3">
      <div className="flex gap-1.5">
        {decisions.map((d, i) => {
          const isCompleted = completedSteps.has(i);
          const isCurrent = i === currentStep;
          const isClickable = isCompleted || isCurrent;

          return (
            <button
              key={d.id}
              type="button"
              onClick={() => isClickable && onNavigate(i)}
              disabled={!isClickable}
              title={isCompleted ? d.title : undefined}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-all",
                isCurrent && "bg-primary text-primary-foreground",
                isCompleted && !isCurrent && "bg-primary/15 text-primary hover:bg-primary/25",
                !isCompleted && !isCurrent && "bg-muted text-muted-foreground/40 cursor-default",
              )}
            >
              {isCompleted ? <CheckIcon className="h-3.5 w-3.5" /> : i + 1}
            </button>
          );
        })}
      </div>

      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{current.title}</span>
          <span className="text-xs text-muted-foreground">{current.points}pts</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {completedSteps.size}/{decisions.length}
        </span>
      </div>
    </div>
  );
}
